import { useCallback, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import { useUserUIPreferences, useAllUsersUIPreferences } from './useUserUIPreferences';
import { OFFICE_TIMEZONE } from '@/locales';
import { useUserLanguage } from './useUserLanguage';

// Auto-detect user's timezone from browser
const detectUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York'; // fallback
  }
};

// Common timezones for selection
export const COMMON_TIMEZONES = [
  { value: 'America/Sao_Paulo', label: 'São Paulo (GMT-3)', region: 'Brazil' },
  { value: 'America/New_York', label: 'New York / Orlando (EST)', region: 'USA' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST)', region: 'USA' },
  { value: 'America/Chicago', label: 'Chicago (CST)', region: 'USA' },
  { value: 'America/Denver', label: 'Denver (MST)', region: 'USA' },
  { value: 'Europe/London', label: 'London (GMT)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (CET)', region: 'Europe' },
  { value: 'Europe/Lisbon', label: 'Lisbon (WET)', region: 'Europe' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)', region: 'Mexico' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)', region: 'Argentina' },
  { value: 'America/Bogota', label: 'Bogotá (COT)', region: 'Colombia' },
  { value: 'America/Lima', label: 'Lima (PET)', region: 'Peru' },
];

export const useUserTimezone = () => {
  const { preferences, isLoading } = useUserUIPreferences();
  const { updatePreferences } = useAllUsersUIPreferences();
  const { dateLocale, dateFormats, use24HourTime } = useUserLanguage();

  // Get user timezone from preferences or auto-detect
  const userTimezone = useMemo(() => {
    return preferences?.timezone || detectUserTimezone();
  }, [preferences?.timezone]);

  // Check if timezone was auto-detected (not saved)
  const isAutoDetected = useMemo(() => !preferences?.timezone, [preferences?.timezone]);

  // Set timezone and persist to preferences
  const setTimezone = useCallback(async (newTimezone: string | null) => {
    if (!preferences?.user_email) return;
    
    await updatePreferences.mutateAsync({
      userEmail: preferences.user_email,
      updates: { timezone: newTimezone }
    });
  }, [preferences?.user_email, updatePreferences]);

  // Auto-persist detected timezone on first load
  useEffect(() => {
    if (preferences?.user_email && !preferences?.timezone && !isLoading) {
      const detected = detectUserTimezone();
      // Auto-save detected timezone
      updatePreferences.mutateAsync({
        userEmail: preferences.user_email,
        updates: { timezone: detected }
      }).catch(console.error);
    }
  }, [preferences?.user_email, preferences?.timezone, isLoading, updatePreferences]);

  // Format date in user's timezone
  const formatInUserTz = useCallback((
    date: Date | string | number,
    formatStr?: string
  ): string => {
    try {
      const dateObj = typeof date === 'string' || typeof date === 'number' 
        ? new Date(date) 
        : date;
      
      if (isNaN(dateObj.getTime())) return '';
      
      const pattern = formatStr || dateFormats.dateTimeShort;
      return formatInTimeZone(dateObj, userTimezone, pattern, { locale: dateLocale });
    } catch (error) {
      console.error('Error formatting date in user timezone:', error);
      return '';
    }
  }, [userTimezone, dateFormats, dateLocale]);

  // Format date in office timezone (Orlando)
  const formatInOfficeTz = useCallback((
    date: Date | string | number,
    formatStr?: string
  ): string => {
    try {
      const dateObj = typeof date === 'string' || typeof date === 'number' 
        ? new Date(date) 
        : date;
      
      if (isNaN(dateObj.getTime())) return '';
      
      // Office always uses 12h format with AM/PM
      const pattern = formatStr || (use24HourTime ? 'h:mm a' : dateFormats.time);
      return formatInTimeZone(dateObj, OFFICE_TIMEZONE, pattern, { locale: dateLocale });
    } catch (error) {
      console.error('Error formatting date in office timezone:', error);
      return '';
    }
  }, [dateFormats, dateLocale, use24HourTime]);

  // Get current time in user's timezone
  const getCurrentUserTime = useCallback((formatStr?: string): string => {
    const pattern = formatStr || dateFormats.time;
    return formatInTimeZone(new Date(), userTimezone, pattern, { locale: dateLocale });
  }, [userTimezone, dateFormats, dateLocale]);

  // Get current time in office timezone
  const getCurrentOfficeTime = useCallback((): string => {
    // Office time always in 12h AM/PM format
    return formatInTimeZone(new Date(), OFFICE_TIMEZONE, 'h:mm a');
  }, []);

  // Convert date to user's timezone (returns Date object)
  const toUserTimezone = useCallback((date: Date | string | number): Date => {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    return toZonedTime(dateObj, userTimezone);
  }, [userTimezone]);

  // Convert date to office timezone (returns Date object)
  const toOfficeTimezone = useCallback((date: Date | string | number): Date => {
    const dateObj = typeof date === 'string' || typeof date === 'number' 
      ? new Date(date) 
      : date;
    return toZonedTime(dateObj, OFFICE_TIMEZONE);
  }, []);

  return {
    userTimezone,
    officeTimezone: OFFICE_TIMEZONE,
    isAutoDetected,
    setTimezone,
    formatInUserTz,
    formatInOfficeTz,
    getCurrentUserTime,
    getCurrentOfficeTime,
    toUserTimezone,
    toOfficeTimezone,
    isLoading,
    commonTimezones: COMMON_TIMEZONES,
  };
};
